import { Guid, RecursivePartial, SM } from '@models/base';
import { FilterTemplate } from '@models/filters';
import { TaskTemplate } from '@models/tasks/task-template';
import { TTaskCardName } from '@models/tasks/cards/_base';
import { BaseState } from './base-state';

export class PrefsState extends BaseState {

    _VER = 11;
    _ref?: string;

    tempInfoHidden: SM<boolean> = {};
    defaultOrgId: string = '';
    favoriteEmojis: SM<number> = {
        '1F44D': 1,
        '1F44E': 1,
        '1F64F': 1,
        '1F440': 1,
        '1F600': 1,
        '1F355': 1,
        '1F37A': 1,
        '1F3C5': 1,
        '2714': 1,
        '274C': 1
    };
    orgBg: SM<string> =  {};
    filterTemplates: FilterTemplate[] = [];

    // Tasks
    taskShowAllFields: boolean = false;
    taskLastProjectId: SM<Guid> = {};
    taskTemplates: TaskTemplate[] = [];
    taskShowNewFullscreen: boolean = false;
    taskShowFullscreen: boolean = false;
    taskListDtRelative: boolean = true;
    taskShowWidth: number = 1000;
    taskTextHeight: number = 500;
    kanbanCard?: TTaskCardName = 'compact';
    kanbanVis: SM<string[]> = {};
    kanbatSettledUp: boolean = false;
    taskListMode: 'table' | 'cards' = 'table';
    projectsListMode: 'table' | 'cards' = 'table';
    employeesListMode: 'table' | 'cards' = 'table';

    // Events
    calendarMode: 'events' | 'both' | 'tasks' | null = 'events';

    // Chat
    chatShowUrlPreview: boolean = true;
    chatShowUrlPreviewImg: boolean = true;
    chatShowFilePreview: boolean = true;
    chatFilePreviewBottom: boolean = false;
    chatFilePreviewSize: 'small' | 'default' | 'large' = 'large';

    // Editor
    editorToolbarShow: SM<boolean> = { _: true };
    editorSendByEnter: SM<boolean> = { _: true, newTask: false, taskAddNote: false, taskTextEdit: false, chatAddComment: true, wikiDoc: false };

    // Tables
    tableResizePolicy: string = 'msword';

    kanbanTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];

    inboxTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];
    inboxTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    inboxTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    inboxTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    favoritesTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];
    favoritesTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    favoritesTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    favoritesTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    inprogressTasksSort: string[] = ['favorite', 'isRead', 'priority', '-modified'];
    inprogressTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    inprogressTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    inprogressTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: false, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    outgoingTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];
    outgoingTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    outgoingTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    outgoingTasksColsVis: SM<boolean> = { priority: true, createdBy: false, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    closedTasksSort: string[] = ['favorite', 'isRead', 'priority', '-modified'];
    closedTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    closedTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    closedTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: false, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    sprintTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];
    sprintTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    sprintTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    sprintTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: true, project: true, modified: true, tags: true, dueDate: true };

    backlogTasksSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];
    backlogTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    backlogTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    backlogTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: true, project: true, modified: true, tags: true, dueDate: true };

    supportTasksSort: string[] = ['isRead', 'status', '-created'];
    supportTasksColsOrder: string[] = ['created', 'status', 'shortId', 'subj'];
    supportTasksColsWidth: SM<number> = { created: 130, status: 120, subj: 400, shortId: 140 };
    supportTasksColsVis: SM<boolean> = { created: true, status: true, subj: true, shortId: true };

    allTasksSort: string[] = ['-created'];
    allTasksColsOrder: string[] = ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'];
    allTasksColsWidth: SM<number> = { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 };
    allTasksColsVis: SM<boolean> = { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true };

    chatsSort: string[] = ['status', '-modified'];
    taskSelectDrawerSort: string[] = ['favorite', 'isRead', 'status', 'priority', '-modified'];

    showFilterPanel: boolean = true;
    grouppedUsers: boolean = false;
    hideReleaseNotes: boolean = false;
    hideTips: boolean = false;
    tourSeen: SM<boolean> = {};

    constructor(json?: RecursivePartial<PrefsState>) {
        super(json);
        this.parsePrefsState(json || {});
    }

    parse(json: RecursivePartial<PrefsState>): PrefsState {
        super.parse(json);
        this.parsePrefsState(json);
        return this;
    }

    private parsePrefsState(json: RecursivePartial<PrefsState>): void {
        PrefsState.assignFields<PrefsState>(this, json, [
            '_ref',
            'tempInfoHidden',
            'defaultOrgId',
            'inboxTasksSort',
            'inboxTasksColsOrder',
            'inboxTasksColsWidth',
            'inboxTasksColsVis',
            'favoritesTasksSort',
            'favoritesTasksColsOrder',
            'favoritesTasksColsWidth',
            'favoritesTasksColsVis',
            'inprogressTasksSort',
            'inprogressTasksColsOrder',
            'inprogressTasksColsWidth',
            'inprogressTasksColsVis',
            'outgoingTasksSort',
            'outgoingTasksColsOrder',
            'outgoingTasksColsWidth',
            'outgoingTasksColsVis',
            'closedTasksSort',
            'closedTasksColsOrder',
            'closedTasksColsWidth',
            'closedTasksColsVis',
            'sprintTasksSort',
            'sprintTasksColsOrder',
            'sprintTasksColsWidth',
            'sprintTasksColsVis',
            'backlogTasksSort',
            'backlogTasksColsOrder',
            'backlogTasksColsWidth',
            'backlogTasksColsVis',
            'supportTasksSort',
            'supportTasksColsOrder',
            'supportTasksColsWidth',
            'supportTasksColsVis',
            'allTasksSort',
            'allTasksColsOrder',
            'allTasksColsWidth',
            'allTasksColsVis',
            'chatsSort',
            'taskSelectDrawerSort',
            'favoriteEmojis',
            'orgBg',
            'taskShowAllFields',
            'taskLastProjectId',
            'taskShowNewFullscreen',
            'taskShowFullscreen',
            'taskListDtRelative',
            'taskShowWidth',
            'editorToolbarShow',
            'editorSendByEnter',
            'tableResizePolicy',
            'chatShowUrlPreview',
            'chatShowUrlPreviewImg',
            'chatShowFilePreview',
            'chatFilePreviewBottom',
            'chatFilePreviewSize',
            'taskTextHeight',
            'showFilterPanel',
            'kanbanCard',
            'kanbanVis',
            'kanbatSettledUp',
            'grouppedUsers',
            'hideReleaseNotes',
            'hideTips',
            'calendarMode',
            'taskListMode',
            'projectsListMode',
            'employeesListMode',
            'tourSeen',
        ]);
        PrefsState.assignClassArrays<PrefsState>(this, json, {
            filterTemplates: FilterTemplate,
            taskTemplates: TaskTemplate
        });
        if (typeof this.tableResizePolicy != 'string') {
            this.tableResizePolicy = 'msword';
        }
    }

}

// export const PREFS_DEFAULTS: { [K in keyof Partial<PrefsState>]: Partial<PrefsState>[K] } = {
//     inboxTasksSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     inboxTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     inboxTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     inboxTasksColsVis: { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     favoritesTasksSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     favoritesTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     favoritesTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     favoritesTasksColsVis: { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     inprogressTasksSort: ['favorite', 'isRead', 'priority', '-modified'],
//     inprogressTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     inprogressTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     inprogressTasksColsVis: { priority: true, createdBy: true, created: true, status: false, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     outgoingTasksSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     outgoingTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     outgoingTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     outgoingTasksColsVis: { priority: true, createdBy: false, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     closedTasksSort: ['favorite', 'isRead', 'priority', '-modified'],
//     closedTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     closedTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     closedTasksColsVis: { priority: true, createdBy: true, created: true, status: false, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     sprintTasksSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     sprintTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags',  'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     sprintTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     sprintTasksColsVis: { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: true, project: true, modified: true, tags: true, dueDate: true },

//     backlogTasksSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     backlogTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags',  'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     backlogTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     backlogTasksColsVis: { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: true, project: true, modified: true, tags: true, dueDate: true },

//     supportTasksSort: ['isRead', 'status', '-created'],
//     supportTasksColsOrder: ['created', 'status', 'shortId', 'subj'],
//     supportTasksColsWidth: { created: 130, status: 120, subj: 400, shortId: 140 },
//     supportTasksColsVis: { created: true, status: true, subj: true, shortId: true },

//     allTasksSort: ['-created'],
//     allTasksColsOrder: ['favorite', 'priority', 'status', 'shortId', 'subj', 'dueDate', 'tags', 'attaches', 'createdBy', 'assignee', 'project', 'created', 'modified'],
//     allTasksColsWidth: { priority: 32, createdBy: 160, created: 130, status: 45, assignee: 160, favorite: 30, subj: 400, shortId: 90, attaches: 45, project: 120, modified: 130, tags: 300, dueDate: 130 },
//     allTasksColsVis: { priority: true, createdBy: true, created: true, status: true, assignee: true, favorite: true, subj: true, shortId: true, attaches: false, project: false, modified: false, tags: true, dueDate: true },

//     chatsSort: ['status', '-modified'],
//     taskSelectDrawerSort: ['favorite', 'isRead', 'status', 'priority', '-modified'],
//     calendarMode: 'events',
//     tableResizePolicy: 'msword',
// };

export const PREFS_DEFAULTS: { [K in keyof Partial<PrefsState>]: Partial<PrefsState>[K] } = new PrefsState();

export const KANBAN_DEFAULT_SORT = ['favorite', 'isRead', 'priority', '-modified'];
